import {useEffect, useMemo, useState} from 'react'
import {useMutation} from '@apollo/client'
import {Badge, Button, Spinner} from 'react-bootstrap'
import {DragDropContext, Draggable, Droppable, DropResult} from 'react-beautiful-dnd'
import {defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'

import {UPDATE_ORDER_STAGES} from '../../../../gql/mutations/pipelineMutations'
import {
  IResOrderStages,
  IStagePipelineSet,
  NodePipelines,
  StageType,
} from '../../../../types/pipelines'
import {parseId} from '../../../../helpers'
import {useDisclourse} from '../../../../hooks/useDisclourse'
import {ModalAddStage} from '../../../stages/ModalAddStage'
import {ModalDeleteStage} from '../../../stages/components/ModalDeleteStage'
import {ModalGoogleLeadsWebhook} from '../../../stages/components/ModalGoogleLeadsWebhook'
import {ModalFacebookLeadsWebhook} from '../../../stages/components/ModalFacebookLeadsWebhook'
import {ModalAddPipeline} from '../ModalAddPipeline'
import {useGetUser} from '../../../../store/userStore'

interface IPipelineInfoTabProps {
  pipeline: NodePipelines
  pipelineNumericId: string
  refetchPipeline: () => void
}

const itM = defineMessages({
  name: {id: 'leads.pipelines.edit.fieldName', defaultMessage: 'Name'},
  organization: {id: 'leads.pipelines.edit.fieldOrg', defaultMessage: 'Organization'},
  noOrganization: {id: 'leads.pipelines.edit.noOrg', defaultMessage: 'No organization'},
  stages: {id: 'leads.pipelines.edit.stages', defaultMessage: 'Stages'},
  stagesHint: {
    id: 'leads.pipelines.edit.stagesHint',
    defaultMessage: 'Drag the rows to change the order of the stages',
  },
  newStage: {id: 'leads.stages.newStage', defaultMessage: 'New Stage'},
  colOrder: {id: 'leads.pipelines.edit.colOrder', defaultMessage: 'Order'},
  colName: {id: 'leads.pipelines.edit.colName', defaultMessage: 'Stage'},
  colType: {id: 'leads.pipelines.edit.colType', defaultMessage: 'Classification'},
  colActions: {id: 'leads.pipelines.edit.colActions', defaultMessage: 'Actions'},
  noStages: {id: 'leads.pipelines.noStages', defaultMessage: 'No stages'},
  toastReordered: {
    id: 'leads.stages.toast.reordered',
    defaultMessage: 'Correctly organized stages',
  },
  editPipeline: {
    id: 'leads.pipelines.edit.editPipeline',
    defaultMessage: 'Edit name / organization',
  },
  googleTooltip: {id: 'leads.webhook.googleTitle', defaultMessage: 'Google Ads Webhook'},
  facebookTooltip: {
    id: 'leads.webhook.facebookTitle',
    defaultMessage: 'Facebook Lead Ads Webhook',
  },
})

const dash = '—'

/** Mueve un elemento de una posición a otra (no intercambia: reinserta). */
const move = (arr: IStagePipelineSet[], from: number, to: number): IStagePipelineSet[] => {
  const result = [...arr]
  const [item] = result.splice(from, 1)
  result.splice(to, 0, item)
  return result
}

export const PipelineInfoTab: React.FC<IPipelineInfoTabProps> = ({
  pipeline,
  pipelineNumericId,
  refetchPipeline,
}) => {
  const intl = useIntl()
  const errMsg = (message?: string) =>
    intl.formatMessage(
      {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
      {message}
    )

  // Los webhooks de Google Ads / Facebook Lead Ads exponen y regeneran el token
  // del endpoint, asi que se reservan a admins. El backend tambien lo exige: los
  // endpoints de configuracion responden 403 a cualquier otro rol.
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const isAdmin = userGroups.includes('Administrador') || userGroups.includes('SuperAdministrador')

  const [gqlOrder, {data: dataOrder, loading: loadingOrder, error: errorOrder}] =
    useMutation<IResOrderStages>(UPDATE_ORDER_STAGES)

  const {isOpen: isOpenPipe, onClose: onClosePipe, onOpen: onOpenPipe} = useDisclourse()
  const {isOpen: isOpenStage, onClose: onCloseStage, onOpen: onOpenStage} = useDisclourse()
  const {isOpen: isOpenDelete, onClose: onCloseDelete, onOpen: onOpenDelete} = useDisclourse()
  const {isOpen: isOpenGoogle, onClose: onCloseGoogle, onOpen: onOpenGoogle} = useDisclourse()
  const {isOpen: isOpenFacebook, onClose: onCloseFacebook, onOpen: onOpenFacebook} = useDisclourse()

  const [webhookStage, setWebhookStage] = useState<{id: string; name: string} | null>(null)
  const [infoStage, setInfoStage] = useState<null | {
    id: string
    name: string
    order: number
    stageType?: StageType | null
  }>(null)

  const [stages, setStages] = useState<IStagePipelineSet[]>([])

  useEffect(() => {
    const edges = pipeline.stagePipelineSet?.edges ?? []
    setStages([...edges].sort((a, b) => a.node.order - b.node.order))
  }, [pipeline])

  const maxOrder = useMemo(
    () => stages.reduce((acc, {node}) => Math.max(acc, node.order), 0),
    [stages]
  )

  useEffect(() => {
    if (errorOrder) toast.error(errMsg(errorOrder.message))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorOrder])

  useEffect(() => {
    if (!dataOrder) return
    const payload = dataOrder.stageOrderPipelineMutation
    if (payload.success) {
      toast.success(intl.formatMessage(itM.toastReordered))
      refetchPipeline()
    } else if (payload.errors?.length) {
      toast.error(errMsg(payload.errors[0]?.message))
      // El backend rechazó el reorden: se vuelve al orden del servidor.
      refetchPipeline()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataOrder])

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return
    const from = result.source.index
    const to = result.destination.index
    if (from === to) return

    const reordered = move(stages, from, to)
    // Optimista: se pinta ya y se confirma con el refetch del servidor.
    setStages(reordered)

    // Se reasignan órdenes 1..n sobre la lista completa. El orden anterior
    // intercambiaba dos filas, lo que en un arrastre de varias posiciones dejaba
    // la lista distinta de lo que el usuario soltó.
    gqlOrder({
      variables: {
        stageOrderData: reordered.map(({node}, idx) => ({
          stageId: Number(parseId(node.id)),
          order: idx + 1,
        })),
      },
    })
  }

  const badgeVariant = (stageType: StageType) =>
    stageType === 'SOLD' ? 'success' : stageType === 'DECLINED' ? 'danger' : 'info'

  return (
    <div className='policy-form'>
      <div className='d-flex align-items-start justify-content-between gap-3 mb-5'>
        <div className='d-flex flex-column flex-md-row gap-6'>
          <div>
            <label className='text-muted fs-8 text-uppercase d-block'>
              {intl.formatMessage(itM.name)}
            </label>
            <span className='fs-4 fw-bold'>{pipeline.name}</span>
          </div>
          <div>
            <label className='text-muted fs-8 text-uppercase d-block'>
              {intl.formatMessage(itM.organization)}
            </label>
            <span className='fs-5'>
              {pipeline.organization?.name ?? (
                <span className='text-muted'>{intl.formatMessage(itM.noOrganization)}</span>
              )}
            </span>
          </div>
        </div>
        <Button
          size='sm'
          variant='outline-primary'
          className='d-flex align-items-center gap-2 flex-shrink-0'
          onClick={onOpenPipe}
        >
          <i className='bi bi-pencil' />
          {intl.formatMessage(itM.editPipeline)}
        </Button>
      </div>

      <div className='d-flex align-items-center justify-content-between gap-2 mb-2'>
        <div>
          <h5 className='mb-0 d-flex align-items-center gap-2'>
            {intl.formatMessage(itM.stages)}
            <span className='badge badge-light-primary'>{stages.length}</span>
            {loadingOrder && <Spinner size='sm' animation='border' />}
          </h5>
          <p className='text-muted fs-8 mb-0'>{intl.formatMessage(itM.stagesHint)}</p>
        </div>
        <Button
          size='sm'
          className='d-flex align-items-center gap-2 flex-shrink-0'
          onClick={() => {
            setInfoStage(null)
            onOpenStage()
          }}
        >
          <i className='bi bi-plus fs-4' />
          {intl.formatMessage(itM.newStage)}
        </Button>
      </div>

      <div className='table-responsive'>
        <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2 mb-0'>
          <thead>
            <tr className='fw-bold text-muted fs-8 text-uppercase'>
              <th style={{width: '40px'}} />
              <th style={{width: '70px'}}>{intl.formatMessage(itM.colOrder)}</th>
              <th>{intl.formatMessage(itM.colName)}</th>
              <th>{intl.formatMessage(itM.colType)}</th>
              <th className='text-end'>{intl.formatMessage(itM.colActions)}</th>
            </tr>
          </thead>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId='stages-table'>
              {(provided) => (
                <tbody ref={provided.innerRef} {...provided.droppableProps}>
                  {stages.length === 0 && (
                    <tr>
                      <td colSpan={5} className='text-muted text-center py-5'>
                        <i className='bi bi-exclamation-circle me-2' />
                        {intl.formatMessage(itM.noStages)}
                      </td>
                    </tr>
                  )}
                  {stages.map(({node}, idx) => (
                    <Draggable
                      key={parseId(node.id)}
                      draggableId={`${parseId(node.id)}`}
                      index={idx}
                    >
                      {(dragProvided, snapshot) => (
                        <tr
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          style={{
                            ...dragProvided.draggableProps.style,
                            background: snapshot.isDragging ? '#f3f3f3' : undefined,
                            display: snapshot.isDragging ? 'table' : undefined,
                          }}
                        >
                          <td
                            {...dragProvided.dragHandleProps}
                            role='button'
                            className='text-muted'
                          >
                            <i className='bi bi-grip-vertical fs-3' />
                          </td>
                          <td className='text-muted'>{idx + 1}</td>
                          <td className='fw-semibold'>{node.name}</td>
                          <td>
                            {node.stageType ? (
                              <Badge bg={badgeVariant(node.stageType)}>{node.stageType}</Badge>
                            ) : (
                              <span className='text-muted'>{dash}</span>
                            )}
                          </td>
                          <td>
                            <div className='d-flex align-items-center justify-content-end gap-1'>
                              {isAdmin && (
                                <>
                                  <button
                                    className='bg-transparent border-0'
                                    title={intl.formatMessage(itM.googleTooltip)}
                                    onClick={() => {
                                      setWebhookStage({id: node.id, name: node.name})
                                      onOpenGoogle()
                                    }}
                                  >
                                    <i className='bi bi-google text-danger fs-5' />
                                  </button>
                                  <button
                                    className='bg-transparent border-0'
                                    title={intl.formatMessage(itM.facebookTooltip)}
                                    onClick={() => {
                                      setWebhookStage({id: node.id, name: node.name})
                                      onOpenFacebook()
                                    }}
                                  >
                                    <i className='bi bi-facebook text-primary fs-5' />
                                  </button>
                                </>
                              )}
                              <button
                                className='bg-transparent border-0'
                                title={intl.formatMessage({
                                  id: 'common.action.edit',
                                  defaultMessage: 'Edit',
                                })}
                                onClick={() => {
                                  setInfoStage({
                                    id: node.id,
                                    name: node.name,
                                    order: node.order,
                                    stageType: node.stageType,
                                  })
                                  onOpenStage()
                                }}
                              >
                                <i className='bi bi-pencil text-primary fs-5' />
                              </button>
                              <button
                                className='bg-transparent border-0'
                                title={intl.formatMessage({
                                  id: 'common.action.delete',
                                  defaultMessage: 'Delete',
                                })}
                                onClick={() => {
                                  setInfoStage({
                                    id: node.id,
                                    name: node.name,
                                    order: node.order,
                                    stageType: node.stageType,
                                  })
                                  onOpenDelete()
                                }}
                              >
                                <i className='bi bi-trash text-danger fs-5' />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </tbody>
              )}
            </Droppable>
          </DragDropContext>
        </table>
      </div>

      {isOpenPipe && (
        <ModalAddPipeline
          isOpen={isOpenPipe}
          onClose={onClosePipe}
          infoEdit={{id: pipeline.id, name: pipeline.name, organization: pipeline.organization}}
          refetch={refetchPipeline}
        />
      )}
      {isOpenStage && (
        <ModalAddStage
          isOpen={isOpenStage}
          onClose={onCloseStage}
          infoEdit={infoStage}
          refetch={refetchPipeline}
          pipelineId={pipelineNumericId}
          maxOrder={maxOrder}
        />
      )}
      {isOpenDelete && infoStage && (
        <ModalDeleteStage
          isOpen={isOpenDelete}
          onClose={onCloseDelete}
          infoEdit={infoStage}
          refetch={refetchPipeline}
        />
      )}
      {isAdmin && isOpenGoogle && webhookStage && (
        <ModalGoogleLeadsWebhook
          isOpen={isOpenGoogle}
          onClose={onCloseGoogle}
          stage={webhookStage}
        />
      )}
      {isAdmin && isOpenFacebook && webhookStage && (
        <ModalFacebookLeadsWebhook
          isOpen={isOpenFacebook}
          onClose={onCloseFacebook}
          stage={webhookStage}
        />
      )}
    </div>
  )
}
